import Loading from "@Components/Loading";
import { useGetLenderDecisionsByStatus } from "@Hooks/useLenderDecisions";
import { DealerDashboardFilters } from "./DealersDashboard";
import styles from "./DealersDashboard.module.scss";

interface LenderDecisionsByStatusSectionProps {
  filters: DealerDashboardFilters;
}

// Status keys come from the backend and aren't a fixed enum, so segment
// colors are looked up by known status name (for stable, semantic color
// across renders) with a neutral fallback for anything unrecognized.
const STATUS_COLOR_MAP: Record<string, string> = {
  Approved: "#60b527",
  Declined: "#eb8190",
  "Conditionally Approved": "#ffbb6c",
  Pending: "#6b97f6",
};
const FALLBACK_COLORS = ["#9e9ca7", "#c7c6cb"];

// Assigns a stable color per status entry: known statuses use their
// semantic color, unrecognized ones cycle through the neutral fallbacks.
const getSegmentColors = (statuses: string[]) => {
  let fallbackIndex = 0;
  return statuses.map((status) => {
    if (STATUS_COLOR_MAP[status]) return STATUS_COLOR_MAP[status];
    const color = FALLBACK_COLORS[fallbackIndex % FALLBACK_COLORS.length];
    fallbackIndex += 1;
    return color;
  });
};

const LenderDecisionsByStatusSection = ({
  filters,
}: LenderDecisionsByStatusSectionProps) => {
  const { data, isLoading } = useGetLenderDecisionsByStatus(filters);

  if (isLoading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "200px",
        }}
      >
        <Loading text="Loading Lender Decisions" />
      </div>
    );
  }

  if (!data) {
    return null;
  }

  const { generalCounts, approvedFundedStatus } = data;
  const statusEntries = Object.entries(generalCounts);
  const total = statusEntries.reduce((sum, [, group]) => sum + group.count, 0);
  const segmentColors = getSegmentColors(statusEntries.map(([status]) => status));

  return (
    <div className={styles.sectionContainer}>
      <div className={styles.sectionTitle}>
        <span className={styles.sectionTitleLabel}>
          Lender Decisions by Status
        </span>
      </div>

      <div className={styles.decisionsBar}>
        {statusEntries.map(([status, group], index) => (
          <div
            key={status}
            className={styles.decisionsBarSegment}
            style={{
              flexBasis: total ? `${(group.count / total) * 100}%` : 0,
              backgroundColor: segmentColors[index],
            }}
          />
        ))}
      </div>

      <div className={styles.decisionsLegend}>
        {statusEntries.map(([status, group], index) => (
          <div key={status} className={styles.decisionsLegendItem}>
            <span
              className={styles.decisionsLegendDot}
              style={{ backgroundColor: segmentColors[index] }}
            />
            <span className={styles.decisionsLegendLabel}>
              {status || "Unknown Status"}
            </span>
            <span className={styles.decisionsLegendValue}>{group.count}</span>
          </div>
        ))}
      </div>

      <div className={styles.sectionContentSplit}>
        <div className={styles.sectionContent}>
          <div className={styles.sectionContentTitle}>
            Approved - Funded Deals
          </div>
          <div className={styles.sectionContentValue}>
            {approvedFundedStatus.fundedCount}
          </div>
        </div>
        <div className={styles.sectionContent}>
          <div className={styles.sectionContentTitle}>
            Approved - Not Funded Deals
          </div>
          <div className={styles.sectionContentValue}>
            {approvedFundedStatus.notFundedCount}
          </div>
        </div>
        <div className={styles.sectionContent}>
          <div className={styles.sectionContentTitle}>
            Approved but funded by other
          </div>
          <div className={styles.sectionContentValue}>
            {approvedFundedStatus.fundedByOther}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LenderDecisionsByStatusSection;
